import { useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import * as Yup from 'yup'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  ChainProtocol,
  Currency,
  READABLE_PROTOCOL,
  READABLE_TOKEN_TYPE,
  TokenCreationNameCharLimit,
  TokenFormData,
  TokenStandardType,
  TokenType,
} from '@archax/shared-types'
import { yupResolver } from '@hookform/resolvers/yup'
import { ButtonGroup } from '@mui/material'
import Button from '@mui/material/Button'
import { FormMode } from '../../../../api/types'
import Select, { SelectOption } from '../../../../components/ui/Select/Select'
import { Switch } from '../../../../components/ui/Switch'
import TextField from '../../../../components/ui/TextField/TextField'
import {
  DISABLED_CHAINS,
  getStandardsByProtocolOptions,
  getRegularStandardsByProtocolOptions,
  PROTOCOLS_WITH_WHITELIST_REQUIRED,
  STANDARDS_WITH_DECIMALS,
  STANDARDS_WITH_PREMINT,
  TOKENS_DOCUMENTS_SIZE_LIMIT,
  TOKENS_DOCUMENTS_SIZE_LIMIT_FORMATTED,
} from '../../../../constants'
import { unparseUnits } from '../../../../util/units'
import { tokenAmountValidator } from '../../../../util/yup-validators'
import { DragDropFiles } from '../DragDropFiles'
import { DisableElement, FeatureMap, HideElement } from '../../../../util/feature-mapping'
import { getDecimalValidator } from '../utils'
import { getCachedData, getTokenSymbolAvailability } from '../../../../api/tokens'

const validationSchema = Yup.object()
  .shape({
    tokenType: Yup.string().oneOf(Object.values(TokenType)).required(),
    name: Yup.string().max(TokenCreationNameCharLimit).required(),
    protocol: Yup.string().required(),
    symbol: Yup.string().when('protocol', ([protocol], schema) => {
      if (protocol === ChainProtocol.XRP_Ledger) {
        schema = schema.min(3).max(20)
      } else if (protocol === ChainProtocol.Stellar) {
        schema = schema.min(3).max(12)
      } else {
        schema = schema.max(6)
      }

      return schema
        .required()
        .test(
          'unique-symbol',
          'An existing token is using this symbol, please define a unique one.',
          async function (value) {
            if (!protocol || !value) {
              return true
            }

            let isAvailable = false
            try {
              const { data } = await getCachedData(['symbol-availability', protocol, value], async () => {
                return getTokenSymbolAvailability({
                  protocol,
                  symbol: value,
                })
              })

              isAvailable = !data.isInUse
            } catch (e) {
              Sentry.captureException(e, {
                extra: {
                  protocol,
                  symbol: value,
                },
              })
            }
            return isAvailable
          },
        )
    }),
    standard: Yup.string().required(),
    numberToCreate: Yup.string().when('decimals', ([decimals]) => {
      if (decimals !== undefined && decimals !== null && !isNaN(decimals) && decimals >= 0 && decimals <= 19) {
        return tokenAmountValidator({ decimals: decimals as any as number })
      }
      return Yup.string().notRequired()
    }),
    notes: Yup.string().max(100),
    currency: Yup.string().when('tokenType', (tokenType, schema) => {
      if (tokenType[0] === TokenType.Regular) {
        return schema.oneOf(Object.values(Currency)).required()
      }
      return schema
    }),
    document: Yup.mixed().optional(),
    kycEnabled: Yup.boolean().optional(),
    blacklistEnabled: Yup.boolean().optional(),
    decimals: Yup.number().when('protocol', ([protocol], schema) => {
      return getDecimalValidator(schema, protocol)
    }),
  })
  .required()

const initialValues = {
  name: '',
  symbol: '',
  protocol: '',
  standard: '',
  numberToCreate: '0',
  notes: '',
  currency: '',
  tokenType: '',
  document: undefined,
  privacyEnabled: false,
  kycEnabled: true,
  blacklistEnabled: false,
  decimals: 0,
} as Record<string, unknown>

interface TokenFormProps {
  mode: FormMode
  onSubmit: (data: TokenFormData) => Promise<void>
  defaultValues?: TokenFormData
}

const poolProtocolOptions = [
  { label: READABLE_PROTOCOL[ChainProtocol.Arbitrum], value: ChainProtocol.Arbitrum },
  { label: READABLE_PROTOCOL[ChainProtocol.Ethereum], value: ChainProtocol.Ethereum },
  { label: READABLE_PROTOCOL[ChainProtocol.Etherlink], value: ChainProtocol.Etherlink },
  { label: READABLE_PROTOCOL[ChainProtocol.Hedera], value: ChainProtocol.Hedera },
  { label: READABLE_PROTOCOL[ChainProtocol.Polygon], value: ChainProtocol.Polygon },
  { label: READABLE_PROTOCOL[ChainProtocol.Stellar], value: ChainProtocol.Stellar },
  { label: READABLE_PROTOCOL[ChainProtocol.XDC], value: ChainProtocol.XDC },
].filter((it) => !DISABLED_CHAINS.includes(it.value))

const tokenProtocolOptions = [
  { label: READABLE_PROTOCOL[ChainProtocol.Algorand], value: ChainProtocol.Algorand },
  { label: READABLE_PROTOCOL[ChainProtocol.Aptos], value: ChainProtocol.Aptos },
  { label: READABLE_PROTOCOL[ChainProtocol.Arbitrum], value: ChainProtocol.Arbitrum },
  { label: READABLE_PROTOCOL[ChainProtocol.Ethereum], value: ChainProtocol.Ethereum },
  { label: READABLE_PROTOCOL[ChainProtocol.Etherlink], value: ChainProtocol.Etherlink },
  { label: READABLE_PROTOCOL[ChainProtocol.Hedera], value: ChainProtocol.Hedera },
  { label: READABLE_PROTOCOL[ChainProtocol.Polygon], value: ChainProtocol.Polygon },
  { label: READABLE_PROTOCOL[ChainProtocol.Solana], value: ChainProtocol.Solana },
  { label: READABLE_PROTOCOL[ChainProtocol.Stellar], value: ChainProtocol.Stellar },
  { label: READABLE_PROTOCOL[ChainProtocol.XDC], value: ChainProtocol.XDC },
  { label: READABLE_PROTOCOL[ChainProtocol.XRP_Ledger], value: ChainProtocol.XRP_Ledger },
].filter((it) => !DISABLED_CHAINS.includes(it.value))

const WHITELIST_REQUIRED_TOOLTIP =
  'This protocol enforces the whitelist on the ledger itself, so it cannot be turned off for this token.'

/**
 * Reports whether the protocol deploys a token whose whitelist can never be turned off.
 *
 * Algorand keeps a per-account `whitelisted` slot in the permissioned-token app, the XRP Ledger
 * issuer runs with `asfRequireAuth`, and a classic Stellar asset is gated by trust line
 * authorisation. None of the three reads `kycEnabled` at creation, so the stored flag has to say
 * `true` or the app and the chain disagree about the token.
 */
function isWhitelistRequired(protocol: unknown): boolean {
  return PROTOCOLS_WITH_WHITELIST_REQUIRED.includes(protocol as ChainProtocol)
}

const protocolOptions = {
  [TokenType.Pool]: poolProtocolOptions,
  [TokenType.Regular]: tokenProtocolOptions,
}

function TokenForm({ onSubmit, defaultValues, mode }: TokenFormProps) {
  const navigate = useNavigate()
  const [standardOptions, setStandardOptions] = useState<SelectOption[]>([])
  const [showPremintField, setShowPremintField] = useState(false)
  const [showDecimalsField, setShowDecimalsField] = useState(false)
  const [protocolSelectOptions, setProtocolSelectOptions] = useState<SelectOption[]>([])
  const [isTokenRegular, setIsTokenRegular] = useState(false)

  const submitButtonLabel = mode === FormMode.Create ? 'Create' : 'Update'

  const {
    getValues,
    setValue,
    control,
    handleSubmit,
    watch,
    formState: { isDirty, isValid, isSubmitting },
    reset,
  } = useForm<TokenFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: defaultValues || initialValues,
    resolver: yupResolver(validationSchema),
  })

  const protocolStandardOptionsMap = useMemo(
    () => ({
      [ChainProtocol.Algorand]: getRegularStandardsByProtocolOptions(ChainProtocol.Algorand),
      [ChainProtocol.Aptos]: getStandardsByProtocolOptions(ChainProtocol.Aptos, isTokenRegular),
      [ChainProtocol.Arbitrum]: getStandardsByProtocolOptions(ChainProtocol.Arbitrum, isTokenRegular),
      [ChainProtocol.Ethereum]: getStandardsByProtocolOptions(ChainProtocol.Ethereum, isTokenRegular),
      [ChainProtocol.Etherlink]: getStandardsByProtocolOptions(ChainProtocol.Etherlink, isTokenRegular),
      [ChainProtocol.Hedera]: getStandardsByProtocolOptions(ChainProtocol.Hedera, isTokenRegular),
      [ChainProtocol.Polygon]: getStandardsByProtocolOptions(ChainProtocol.Polygon, isTokenRegular),
      [ChainProtocol.Solana]: getRegularStandardsByProtocolOptions(ChainProtocol.Solana),
      [ChainProtocol.Stellar]: getStandardsByProtocolOptions(ChainProtocol.Stellar, isTokenRegular),
      [ChainProtocol.XDC]: getStandardsByProtocolOptions(ChainProtocol.XDC, isTokenRegular),
      [ChainProtocol.XRP_Ledger]: getRegularStandardsByProtocolOptions(ChainProtocol.XRP_Ledger),
    }),
    [isTokenRegular],
  )

  useEffect(() => {
    if (mode === FormMode.Edit) {
      setProtocolSelectOptions(protocolOptions[defaultValues?.tokenType as TokenType.Pool | TokenType.Regular])
      setIsTokenRegular(defaultValues?.tokenType === TokenType.Regular)
      const newOptions = protocolStandardOptionsMap[defaultValues?.protocol as ChainProtocol]
      setStandardOptions(newOptions)
      setValue('document', defaultValues?.tempDocuments)
      setValue('standard', defaultValues!.standard)
      setValue('numberToCreate', unparseUnits(defaultValues!.numberToCreate!, defaultValues!.decimals!))
    }
  }, [defaultValues, mode, protocolStandardOptionsMap, setValue, getValues])

  useEffect(() => {
    watch((value, { name }) => {
      if (name === 'protocol') {
        const newOptions = protocolStandardOptionsMap[value.protocol as ChainProtocol]
        setStandardOptions(newOptions)
        // The switch is only greyed out for these protocols, which leaves whatever the operator
        // picked on a previous protocol in the form. Creating with `kycEnabled: false` on a ledger
        // that always enforces the whitelist makes `grantKYC` skip the on-chain call, so holders
        // are recorded in the app but never authorised on chain.
        if (isWhitelistRequired(value.protocol)) {
          setValue('kycEnabled', true)
        }
      }
      if (name === 'standard') {
        const allowPremint = STANDARDS_WITH_PREMINT.includes(value.standard as TokenStandardType)
        setShowPremintField(allowPremint)
        const allowDecimals = STANDARDS_WITH_DECIMALS.includes(value.standard as TokenStandardType)
        setShowDecimalsField(allowDecimals)
      }
      if (name === 'tokenType') {
        setProtocolSelectOptions(protocolOptions[value.tokenType as TokenType.Pool | TokenType.Regular])
        setIsTokenRegular(value.tokenType === TokenType.Regular)
        reset({ ...initialValues, tokenType: value.tokenType })
        setStandardOptions([])
      }
      if (name === 'document') {
        if (value.document) {
          const documentsSize = Object.values(value.document).reduce((acc: number, file: any) => acc + file.size, 0)
          if (documentsSize > TOKENS_DOCUMENTS_SIZE_LIMIT) {
            toast.error(`Please choose a file that is ${TOKENS_DOCUMENTS_SIZE_LIMIT_FORMATTED} or smaller`)
            reset({ ...initialValues, document: undefined })
          }
        }
      }
    })
  }, [watch, reset, protocolStandardOptionsMap, getValues, setValue])

  return (
    <form
      style={{
        marginTop: '32px',
      }}
      onSubmit={handleSubmit(async (data) => {
        await onSubmit(data)
        reset({ ...initialValues })
        setShowPremintField(false)
        setShowDecimalsField(false)
        setIsTokenRegular(false)
      })}
    >
      <Select
        disabled={mode === FormMode.Edit}
        label="Token type"
        name="tokenType"
        control={control}
        options={[
          { label: READABLE_TOKEN_TYPE[TokenType.Regular], value: TokenType.Regular },
          { label: READABLE_TOKEN_TYPE[TokenType.Pool], value: TokenType.Pool },
        ]}
      />
      <Select name="protocol" control={control} label="Protocol" options={protocolSelectOptions} />
      <Select name="standard" control={control} label="Standard" options={standardOptions} />

      <HideElement skipIfEmpty={false} protocol={getValues('protocol')} feature={FeatureMap.PRIVACY}>
        <Switch
          name="privacyEnabled"
          control={control}
          label="Enable privacy"
          tooltip="Add privacy with Silent Data [Rollup]"
        />
      </HideElement>
      <DisableElement protocol={getValues('protocol')} feature={FeatureMap.WHITELIST}>
        <Switch
          name="kycEnabled"
          control={control}
          label="Whitelist"
          tooltip={isWhitelistRequired(getValues('protocol')) ? WHITELIST_REQUIRED_TOOLTIP : undefined}
        />
      </DisableElement>
      <DisableElement protocol={getValues('protocol')} feature={FeatureMap.BLACKLIST}>
        <Switch name="blacklistEnabled" control={control} label="Blacklist" />
      </DisableElement>

      <TextField name="name" control={control} label="Name" />
      <TextField name="symbol" control={control} label="Symbol" />
      {showDecimalsField && <TextField name="decimals" type="number" control={control} label="Decimals" />}
      {showPremintField && (
        <TextField name="numberToCreate" type="number" control={control} label="Number to create (optional)" />
      )}
      {isTokenRegular && (
        <Select
          name="currency"
          control={control}
          label="Currency"
          options={Object.values(Currency).map((currencyCode) => ({
            label: currencyCode,
            value: currencyCode,
          }))}
        />
      )}

      <TextField name="notes" control={control} label="Notes (optional)" />

      <HideElement protocol={getValues('protocol')} feature={FeatureMap.UPLOAD_DOCS}>
        <DragDropFiles
          getValues={getValues}
          setvalue={setValue}
          mode={mode}
          oldDocuments={defaultValues?.tempDocuments}
        />
      </HideElement>

      <ButtonGroup fullWidth>
        {mode === FormMode.Edit && (
          <Button
            size="large"
            fullWidth
            onClick={() => {
              navigate('/tokens/requests/edit')
            }}
          >
            Cancel
          </Button>
        )}
        <Button
          disabled={!isDirty || !isValid || isSubmitting}
          type="submit"
          variant="contained"
          size="large"
          color="primary"
          fullWidth
        >
          {submitButtonLabel}
        </Button>
      </ButtonGroup>
    </form>
  )
}
export default TokenForm
